import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { FileText, ImagePlus, LayoutDashboard, LogOut, MonitorPlay, Palette, Plus, Ticket, Trash2, Trophy, Users } from "lucide-react";
import { GamesTab, MembersTab, ScreensTab } from "../components/club/ClubAdminTabs";
import { BACKEND_URL } from "../lib/api";
import { getClub } from "../data/clubs";
import { DAYS, NOTICE_KINDS } from "../components/club/ClubChrome";
import { GAME_LABELS } from "../components/club/ClubBoards";
import { EventCard } from "./AffiliatePage";

// The universal club sign-in. The same box answers on /crp/admin, /asp/admin, /qbp/admin — the credentials
// decide which club's dashboard you land on, and you are moved to your own club's URL if you used another's.
const KEY = "pfp_affiliate_token";
// Players Room staff hold one token per club (they can open every club's admin); a club's own login uses KEY
const staffKey = () => `${KEY}:${window.location.pathname.split("/")[1] || ""}`;
const readToken = () => localStorage.getItem(staffKey()) || localStorage.getItem(KEY);
const dropToken = () => { localStorage.removeItem(staffKey()); localStorage.removeItem(KEY); };
const money = (c) => `A$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
const day = (s) => (s ? new Date(s).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "—");
const api = () => axios.create({ baseURL: BACKEND_URL, headers: { Authorization: `Bearer ${readToken() || ""}` } });

const Login = ({ club, onIn }) => {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/affiliate/login`, { email, password: pw, slug: club?.slug || "" });
      localStorage.setItem(data.staff ? staffKey() : KEY, data.access_token);
      onIn();
    } catch (x) {
      const d = x?.response?.data?.detail;
      setErr(typeof d === "string" ? d : d?.detail || "Email or password is not right");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="min-h-screen flex items-center justify-center p-6" data-testid="club-admin-login-page">
      <form onSubmit={submit} className="club-card p-8 w-full max-w-sm flex flex-col gap-4" data-testid="club-admin-login">
        {club
          ? <img src={club.logo} alt={club.name} className="h-10 self-center object-contain" />
          : <div className="club-display text-xl uppercase text-center">Club sign-in</div>}
        <div className="club-display text-lg uppercase text-center">Club dashboard</div>
        <p className="text-xs club-soft text-center">
          Club operators only. Your sign-in takes you to your own club's dashboard — your room, your games, your codes.
        </p>
        <label className="text-xs club-soft">Email
          <input data-testid="club-admin-email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" required
            className="club-input mt-1 w-full px-3 py-2 text-sm" />
        </label>
        <label className="text-xs club-soft">Password
          <input data-testid="club-admin-password" value={pw} onChange={(e) => setPw(e.target.value)} type="password" required
            className="club-input mt-1 w-full px-3 py-2 text-sm" />
        </label>
        {err && <div data-testid="club-admin-login-error" className="text-xs text-red-400">{err}</div>}
        <button data-testid="club-admin-login-submit" disabled={busy} type="submit"
          className="club-btn px-6 py-2.5 text-xs uppercase tracking-[0.2em] disabled:opacity-50">
          {busy ? "Signing in…" : "Sign in"}
        </button>
        <Link to={club ? `/${club.slug}` : "/"} className="text-[11px] club-soft text-center underline underline-offset-4"
          data-testid="club-admin-back">Back to the club room</Link>
      </form>
    </div>
  );
};

const Lines = ({ label, hint, value, max, onChange, testId }) => (
  <label className="flex flex-col gap-1" data-testid={testId}>
    <span className="text-[10px] uppercase tracking-[0.22em] club-accent-text">{label}</span>
    <span className="text-[11px] club-soft">{hint}</span>
    <textarea rows={Math.min(Math.max((value || "").split("\n").length + 1, 3), 10)} value={value} maxLength={max}
      onChange={(e) => onChange(e.target.value)} className="club-input px-3 py-2 text-sm font-mono" />
  </label>
);

// Self-serve: the words on the club's public room. Images come next, once the layout is signed off.
const RoomTab = ({ club, room, tier, onSaved }) => {
  const holdemOnly = (tier || "TIER1") === "TIER1";   // Core plan: Hold'em, tournaments, eight seats
  const maxSeats = holdemOnly ? 8 : 10;
  const [blurb, setBlurb] = useState(room?.blurb || "");
  const [ticker, setTicker] = useState((room?.ticker || []).join("\n"));
  const [venues, setVenues] = useState(room?.venues?.length ? room.venues : []);
  const [notices, setNotices] = useState(room?.notices?.length ? room.notices : []);
  const [games, setGames] = useState(room?.games?.length ? room.games : []);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const setVenue = (i, patch) => setVenues(venues.map((v, n) => (n === i ? { ...v, ...patch } : v)));
  const setNotice = (i, patch) => setNotices(notices.map((v, n) => (n === i ? { ...v, ...patch } : v)));
  const setGame = (i, patch) => setGames(games.map((v, n) => (n === i ? { ...v, ...patch } : v)));

  const save = async () => {
    setBusy(true);
    setMsg("");
    try {
      const { data } = await api().patch("/api/affiliate/room", {
        blurb,
        ticker: ticker.split("\n").map((s) => s.trim()).filter(Boolean).slice(0, 10),
        venues: venues.filter((v) => (v.name || "").trim()).slice(0, 60),
        notices: notices.filter((n) => (n.title || "").trim()).slice(0, 12),
        games: games.filter((g) => (g.name || "").trim()).slice(0, 20),
      });
      onSaved(data.room);
      setMsg("Saved — your room is updated.");
    } catch {
      setMsg("Could not save that — try again shortly.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="flex flex-col gap-5" data-testid="club-admin-room">
      <div className="club-card p-5 flex flex-col gap-5">
        <Lines label="Ticker — announcements & upcoming series"
          hint="One line per message. These run across the top of your room — change them whenever you like. Up to 10."
          value={ticker} max={2000} onChange={setTicker} testId="club-room-ticker" />
        <Lines label="About your club" hint="The paragraph under your logo. 600 characters." value={blurb} max={600}
          onChange={setBlurb} testId="club-room-blurb" />
      </div>

      <div className="club-card p-5 flex flex-col gap-4" data-testid="club-room-games">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div className="flex flex-col">
            <span className="text-[10px] uppercase tracking-[0.22em] club-accent-text">Your tables — playing now</span>
            <span className="text-[11px] club-soft">The games in the middle of your room — each one opens as a real tournament in the lobby an hour before it starts. You set the name, seats, date, start time and prize.</span>
            {holdemOnly && <span className="text-[11px] club-accent-text" data-testid="club-tier-note">Your plan runs Hold\'em tournaments up to 8-max.</span>}
          </div>
          <button data-testid="club-game-add"
            onClick={() => setGames([...games, { name: "", game: "HOLDEM", seats: maxSeats > 8 ? 8 : 8, free: true, buyin: "", prize: "", date: "", time: "" }])}
            className="club-chip px-4 py-2 text-[11px] uppercase tracking-[0.18em] flex items-center gap-2">
            <Plus className="w-3.5 h-3.5" /> Add a table
          </button>
        </div>
        {games.map((g, i) => (
          <div key={i} className="flex gap-2 flex-wrap items-center" data-testid={`club-game-row-${i}`}>
            <input value={g.name} maxLength={60} placeholder="Table name" onChange={(e) => setGame(i, { name: e.target.value })}
              data-testid={`club-game-name-${i}`} className="club-input px-3 py-2 text-sm flex-1 min-w-[12rem]" />
            <select value={g.game} onChange={(e) => setGame(i, { game: e.target.value })} disabled={holdemOnly}
              data-testid={`club-game-variant-${i}`} className="club-input px-3 py-2 text-sm disabled:opacity-60">
              {Object.entries(GAME_LABELS).filter(([k]) => !holdemOnly || k === "HOLDEM").map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
            <select value={g.seats} onChange={(e) => setGame(i, { seats: Number(e.target.value) })}
              data-testid={`club-game-seats-${i}`} className="club-input px-3 py-2 text-sm">
              {[2, 6, 8, 9, 10].filter((n) => n <= maxSeats).map((n) => <option key={n} value={n}>{n}-max</option>)}
            </select>
            <input type="date" value={g.date} onChange={(e) => setGame(i, { date: e.target.value })}
              data-testid={`club-game-date-${i}`} className="club-input px-3 py-2 text-sm" />
            <input value={g.time} maxLength={24} placeholder="7:30pm" onChange={(e) => setGame(i, { time: e.target.value })}
              data-testid={`club-game-time-${i}`} className="club-input px-3 py-2 text-sm w-28" />
            <label className="flex items-center gap-2 text-xs club-soft">
              <input type="checkbox" checked={g.free} onChange={(e) => setGame(i, { free: e.target.checked })}
                data-testid={`club-game-free-${i}`} /> Freeroll
            </label>
            {!g.free && (
              <input value={g.buyin} maxLength={40} placeholder="Buy-in" onChange={(e) => setGame(i, { buyin: e.target.value })}
                data-testid={`club-game-buyin-${i}`} className="club-input px-3 py-2 text-sm w-32" />
            )}
            <input value={g.prize} maxLength={90} placeholder="Prize (ticket to…)" onChange={(e) => setGame(i, { prize: e.target.value })}
              data-testid={`club-game-prize-${i}`} className="club-input px-3 py-2 text-sm flex-1 min-w-[12rem]" />
            <button data-testid={`club-game-remove-${i}`} onClick={() => setGames(games.filter((_, n) => n !== i))}
              className="club-chip p-2"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        {!games.length && <span className="text-xs club-soft">No tables set up — your room shows "built to the club's order" until you add one.</span>}
      </div>

      <div className="club-card p-5 flex flex-col gap-4" data-testid="club-room-venues">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div className="flex flex-col">
            <span className="text-[10px] uppercase tracking-[0.22em] club-accent-text">Your week — venues</span>
            <span className="text-[11px] club-soft">Where you run live poker, with each game's start time, buy-in and prize pool. Set "When they run it" for anything that is not weekly.</span>
          </div>
          <button data-testid="club-venue-add" onClick={() => setVenues([...venues, { day: "MON", name: "", time: "", rego: "", late: "", buyin: "", prize: "", blinds: "", note: "" }])}
            className="club-chip px-4 py-2 text-[11px] uppercase tracking-[0.18em] flex items-center gap-2">
            <Plus className="w-3.5 h-3.5" /> Add a venue
          </button>
        </div>
        {venues.map((vn, i) => (
          <div key={i} className="flex gap-2 flex-wrap items-center" data-testid={`club-venue-row-${i}`}>
            <select value={vn.day} onChange={(e) => setVenue(i, { day: e.target.value })}
              data-testid={`club-venue-day-${i}`} className="club-input px-3 py-2 text-sm">
              {DAYS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
              <option value="ANY">When they run it</option>
            </select>
            <input value={vn.name} maxLength={80} placeholder="Venue name" onChange={(e) => setVenue(i, { name: e.target.value })}
              data-testid={`club-venue-name-${i}`} className="club-input px-3 py-2 text-sm flex-1 min-w-[12rem]" />
            <input value={vn.time} maxLength={24} placeholder="Start (7:30pm)" onChange={(e) => setVenue(i, { time: e.target.value })}
              data-testid={`club-venue-time-${i}`} className="club-input px-3 py-2 text-sm w-32" />
            <input value={vn.rego || ""} maxLength={24} placeholder="Rego (7:00pm)" onChange={(e) => setVenue(i, { rego: e.target.value })}
              data-testid={`club-venue-rego-${i}`} className="club-input px-3 py-2 text-sm w-32" />
            <input value={vn.late || ""} maxLength={24} placeholder="Late rego (8:30pm)" onChange={(e) => setVenue(i, { late: e.target.value })}
              data-testid={`club-venue-late-${i}`} className="club-input px-3 py-2 text-sm w-36" />
            <input value={vn.buyin || ""} maxLength={40} placeholder="Buy-in ($30 + $10)" onChange={(e) => setVenue(i, { buyin: e.target.value })}
              data-testid={`club-venue-buyin-${i}`} className="club-input px-3 py-2 text-sm w-40" />
            <input value={vn.prize || ""} maxLength={40} placeholder="Prize pool ($2,000 GTD)" onChange={(e) => setVenue(i, { prize: e.target.value })}
              data-testid={`club-venue-prize-${i}`} className="club-input px-3 py-2 text-sm w-44" />
            <input value={vn.blinds || ""} maxLength={40} placeholder="Blinds (100/200 · 20 min)" onChange={(e) => setVenue(i, { blinds: e.target.value })}
              data-testid={`club-venue-blinds-${i}`} className="club-input px-3 py-2 text-sm w-44" />
            <input value={vn.note} maxLength={60} placeholder="Note (fortnightly)" onChange={(e) => setVenue(i, { note: e.target.value })}
              data-testid={`club-venue-note-${i}`} className="club-input px-3 py-2 text-sm w-40" />
            <button data-testid={`club-venue-remove-${i}`} onClick={() => setVenues(venues.filter((_, n) => n !== i))}
              className="club-chip p-2"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        {!venues.length && <span className="text-xs club-soft">Nothing listed — your room shows the schedule we set up for you until you add your own.</span>}
      </div>

      <div className="club-card p-5 flex flex-col gap-4" data-testid="club-room-notices">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div className="flex flex-col">
            <span className="text-[10px] uppercase tracking-[0.22em] club-accent-text">The board — news, offers, series</span>
            <span className="text-[11px] club-soft">Anything you want your players to see. Up to 12 boards.</span>
          </div>
          <button data-testid="club-notice-add" onClick={() => setNotices([...notices, { kind: "NEWS", title: "", body: "" }])}
            className="club-chip px-4 py-2 text-[11px] uppercase tracking-[0.18em] flex items-center gap-2">
            <Plus className="w-3.5 h-3.5" /> Add a board
          </button>
        </div>
        {notices.map((n, i) => (
          <div key={i} className="flex gap-2 flex-wrap items-start" data-testid={`club-notice-row-${i}`}>
            <select value={n.kind} onChange={(e) => setNotice(i, { kind: e.target.value })}
              data-testid={`club-notice-kind-${i}`} className="club-input px-3 py-2 text-sm">
              {NOTICE_KINDS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
            <div className="flex flex-col gap-2 flex-1 min-w-[18rem]">
              <input value={n.title} maxLength={90} placeholder="Headline" onChange={(e) => setNotice(i, { title: e.target.value })}
                data-testid={`club-notice-title-${i}`} className="club-input px-3 py-2 text-sm" />
              <textarea value={n.body} maxLength={400} rows={2} placeholder="The detail (optional)"
                onChange={(e) => setNotice(i, { body: e.target.value })}
                data-testid={`club-notice-body-${i}`} className="club-input px-3 py-2 text-sm" />
            </div>
            <button data-testid={`club-notice-remove-${i}`} onClick={() => setNotices(notices.filter((_, x) => x !== i))}
              className="club-chip p-2"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        {!notices.length && <span className="text-xs club-soft">Nothing posted — your room shows the boards we set up for you until you add your own.</span>}
      </div>

      <div className="flex items-center gap-4 flex-wrap">
        <button data-testid="club-room-save" disabled={busy} onClick={save}
          className="club-btn px-6 py-2.5 text-xs uppercase tracking-[0.2em] disabled:opacity-50">
          {busy ? "Saving…" : "Save your room"}
        </button>
        {club && <Link to={`/${club.slug}`} target="_blank" rel="noreferrer" data-testid="club-room-preview"
          className="club-chip px-5 py-2.5 text-xs uppercase tracking-[0.2em]">View your room</Link>}
        {msg && <span className="text-xs club-soft" data-testid="club-room-msg">{msg}</span>}
      </div>

      <div className="club-card p-5 flex items-start gap-4" data-testid="club-room-uploads-soon">
        <ImagePlus className="w-5 h-5 club-accent-text shrink-0 mt-0.5" />
        <div className="flex flex-col gap-1 text-sm">
          <span className="club-display uppercase">Your logo, posters and photos</span>
          <span className="club-soft text-xs">
            Upload slots land here next — one per section, so you can swap a poster or a tournament tile yourself.
            Hold off sending artwork until we confirm the sizes for each slot.
          </span>
        </div>
      </div>
    </section>
  );
};

const Stat = ({ label, value, sub, testId }) => (
  <div className="club-card p-4 flex flex-col gap-1" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-[0.22em] club-accent-text">{label}</div>
    <div className="club-display text-2xl uppercase leading-none">{value}</div>
    {sub && <div className="text-xs club-soft">{sub}</div>}
  </div>
);

const TABS = [["overview", "Overview", LayoutDashboard], ["room", "My room", Palette], ["screens", "My screens", MonitorPlay],
  ["members", "Members", Users], ["games", "Build a tournament", Plus], ["events", "My tournaments", Trophy], ["codes", "Winner codes", Ticket], ["invoices", "Invoices", FileText]];

export default function ClubAdminPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const club = getClub(slug);
  const [token, setTokenState] = useState(() => readToken());
  const [data, setData] = useState(null);
  const [events, setEvents] = useState(null);
  const [codes, setCodes] = useState(null);
  const [tab, setTab] = useState("overview");
  const [err, setErr] = useState("");
  const [busyEvent, setBusyEvent] = useState("");

  const load = useCallback(async () => {
    try {
      const c = api();
      const [d, e, k] = await Promise.all([
        c.get("/api/affiliate/dashboard"), c.get("/api/affiliate/tournaments"), c.get("/api/affiliate/codes")]);
      setData(d.data);
      setEvents(e.data);
      setCodes(k.data);
    } catch (x) {
      if (x?.response?.status === 401) { dropToken(); setTokenState(null); }
      else setErr("Could not load your dashboard — try again shortly.");
    }
  }, []);

  useEffect(() => { if (token) load(); }, [token, load]);

  // Universal box: if these credentials belong to another club, move them to their own URL.
  const mine = data?.affiliate?.slug;
  useEffect(() => {
    if (mine && mine !== slug) nav(`/${mine}/admin`, { replace: true });
  }, [mine, slug, nav]);

  const theme = club?.theme;
  const styleVars = theme
    ? {
        "--c-ground": theme.ground, "--c-surface": theme.surface, "--c-line": theme.line, "--c-ink": theme.ink,
        "--c-ink-soft": theme.inkSoft, "--c-accent": theme.accent, "--c-accent-deep": theme.accentDeep,
        "--c-on-accent": theme.onAccent, "--c-display": theme.display, "--c-body": theme.body,
        "--c-tracking": theme.displayTracking, "--c-radius": theme.radius,
      }
    : undefined;

  const approveEvent = async (ev, approved, note) => {
    setBusyEvent(ev.event_id);
    try {
      await api().post(`/api/affiliate/tournaments/${ev.event_id}/approve`, { approved, note: note || "" });
      const e = await api().get("/api/affiliate/tournaments");
      setEvents(e.data);
    } catch {
      setErr("Could not save that — try again shortly.");
    } finally { setBusyEvent(""); }
  };

  const signOut = () => { dropToken(); setTokenState(null); setData(null); setTab("overview"); };

  if (!token) {
    return <div className="club-room min-h-screen" style={styleVars}>
      <Login club={club} onIn={() => setTokenState(readToken())} />
    </div>;
  }
  if (!data) {
    return <div className="club-room min-h-screen flex items-center justify-center text-sm club-soft" style={styleVars}
      data-testid="club-admin-loading">{err || "Loading your dashboard…"}</div>;
  }

  const a = data.affiliate;
  const e = data.earnings;

  return (
    <div className="club-room min-h-screen px-5 md:px-10 py-6 flex flex-col gap-6" style={styleVars}
      data-testid="club-admin-dashboard">
      <header className="flex items-center justify-between gap-4 flex-wrap club-bar -mx-5 md:-mx-10 px-5 md:px-10 py-4">
        <div className="flex items-center gap-4">
          {club && <img src={club.logo} alt={a.business_name} className="h-9 object-contain" />}
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] club-accent-text">Club dashboard</div>
            <h1 className="club-display text-xl uppercase" data-testid="club-admin-name">{a.business_name}</h1>
            <div className="text-xs club-soft">
              Tracking code <span className="font-mono club-accent-text" data-testid="club-admin-tracking">{a.tracking_code || a.referral_code}</span>
              {a.code_prefix && <> · winner-code prefix <span className="font-mono club-accent-text">{a.code_prefix}</span></>}
              {a.slug && <> · playersroom.au/{a.slug}</>}
            </div>
          </div>
        </div>
        <button data-testid="club-admin-signout" onClick={signOut}
          className="club-chip px-4 py-2 text-[11px] uppercase tracking-[0.18em] flex items-center gap-2">
          <LogOut className="w-3.5 h-3.5" /> Sign out
        </button>
      </header>

      <nav className="flex gap-2 flex-wrap">
        {TABS.map(([k, label, Icon]) => (
          <button key={k} data-testid={`club-admin-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2 text-[11px] uppercase tracking-[0.18em] flex items-center gap-2 ${tab === k ? "club-btn" : "club-chip"}`}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </nav>

      {!!(events?.awaiting || []).length && (
        <button data-testid="club-admin-awaiting" onClick={() => setTab("events")}
          className="club-card p-4 text-left text-sm">
          <span className="club-display uppercase">{events.awaiting.length} tournament{events.awaiting.length > 1 ? "s" : ""} waiting on your approval</span>
          <span className="block text-xs club-soft">Check the buy-in, date, blinds and winner codes, then tick the box — players only see it once you do.</span>
        </button>
      )}

      {tab === "overview" && (
        <section className="grid gap-4 md:grid-cols-4" data-testid="club-admin-overview">
          <Stat testId="club-stat-tier" label="Your plan" value={e.tier_name} sub={`${money(e.monthly_cents)} a month`} />
          <Stat testId="club-stat-seats" label="Seats procured" value={e.seats_bought || 0} sub={`${money(e.tickets_cents)} at face value`} />
          <Stat testId="club-stat-owed" label="Seats owed to you" value={money(e.tickets_owed_cents)} sub="paid on the weekly run" />
          <Stat testId="club-stat-subscription" label="Subscription outstanding" value={money(e.saas_owed_cents)} sub="invoiced monthly" />
          <Stat testId="club-stat-players" label="Players on your code" value={e.player_count} sub={`signed up with ${a.tracking_code || a.referral_code}`} />
          <div className="club-card p-4 md:col-span-3 text-xs club-soft flex flex-col gap-1">
            <span className="club-display uppercase text-sm">How your account works</span>
            <p>Your club pays a flat monthly subscription for the technology — {e.tier_name}, {money(e.monthly_cents)} a month.
              Players Room takes nothing out of your prize pools and nothing from what your players spend.</p>
            <p>Tournament seats we buy from you are paid at face value on the weekly run
              ({data.schedule?.weekday || "Wednesday"}, next {day(data.schedule?.next_run)}), under a Recipient Created
              Tax Invoice.</p>
          </div>
        </section>
      )}

      {tab === "room" && <RoomTab club={club} room={a.room} tier={e.tier} onSaved={(room) => setData({ ...data, affiliate: { ...a, room } })} />}

      {tab === "screens" && <ScreensTab api={api} />}
      {tab === "members" && <MembersTab api={api} />}
      {tab === "games" && <GamesTab api={api} />}

      {tab === "events" && (
        <section className="flex flex-col gap-4" data-testid="club-admin-events">
          {(events?.events || []).map((ev) => (
            <EventCard key={ev.event_id} ev={ev} prefix={a.code_prefix} onApprove={approveEvent} busy={busyEvent === ev.event_id} />
          ))}
          {!events?.events?.length && <p className="text-xs club-soft">No tournaments are flagged as yours yet — ask Players Room to build your next event.</p>}
        </section>
      )}

      {tab === "codes" && (
        <section className="club-card overflow-hidden" data-testid="club-admin-codes">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.2em] club-accent-text">
              <tr><th className="text-left p-3">Code</th><th className="text-left p-3">Event</th><th className="text-left p-3">Winner</th><th className="text-right p-3">Place</th><th className="text-left p-3">Redeemed</th></tr>
            </thead>
            <tbody>
              {(codes?.codes || []).map((c) => (
                <tr key={c.code} data-testid={`club-code-${c.code}`} className="border-t" style={{ borderColor: "var(--c-line)" }}>
                  <td className="p-3 font-mono club-accent-text">{c.code}</td><td className="p-3">{c.event}</td>
                  <td className="p-3">{c.winner_name}</td><td className="p-3 text-right">{c.place}</td>
                  <td className="p-3 club-soft">{c.redeemed ? `Yes · ${day(c.redeemed_at)}` : "Not yet"}</td>
                </tr>
              ))}
              {!codes?.codes?.length && <tr><td colSpan={5} className="p-4 text-center club-soft text-xs">No winner codes issued yet.</td></tr>}
            </tbody>
          </table>
        </section>
      )}

      {tab === "invoices" && (
        <section className="club-card overflow-hidden" data-testid="club-admin-invoices">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.2em] club-accent-text">
              <tr><th className="text-left p-3">Invoice</th><th className="text-left p-3">Period</th><th className="text-right p-3">Total</th><th className="text-left p-3">Status</th></tr>
            </thead>
            <tbody>
              {(data.invoices || []).map((p) => (
                <tr key={p.payout_id} data-testid={`club-invoice-${p.invoice_no}`} className="border-t" style={{ borderColor: "var(--c-line)" }}>
                  <td className="p-3 font-mono club-accent-text">{p.invoice_no}</td>
                  <td className="p-3 club-soft">{day(p.period_start)} – {day(p.period_end)}</td>
                  <td className="p-3 text-right">{money(p.total_cents)}</td>
                  <td className="p-3 club-soft">{p.status === "paid" ? `Paid ${day(p.paid_at)}` : `Scheduled ${day(p.due_at)}`}</td>
                </tr>
              ))}
              {!(data.invoices || []).length && <tr><td colSpan={4} className="p-4 text-center club-soft text-xs">No invoices yet.</td></tr>}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
